// src/pages/StackRanking.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

const TEAL = '#00d4a0';
const MEDALS = ['🥇', '🥈', '🥉'];

export default function StackRanking() {
  const [orders, setOrders] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month'); // month | alltime | week
  const [sortBy, setSortBy] = useState('deals'); // deals | commission | completed

  useEffect(() => {
    async function load() {
      const [ordSnap, repSnap] = await Promise.all([
        getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'users')),
      ]);
      setOrders(ordSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setReps(repSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }
    load();
  }, []);

  function getDateRange() {
    const now = new Date();
    if (period === 'month') return { start: startOfMonth(now), end: endOfMonth(now) };
    if (period === 'lastmonth') return { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)) };
    if (period === 'week') {
      const start = new Date(now); start.setDate(now.getDate() - 7);
      return { start, end: now };
    }
    return { start: new Date(0), end: now };
  }

  const { start, end } = getDateRange();

  const filteredOrders = orders.filter(o => {
    // Use writtenDate for stack ranking
    let d;
    if (o.writtenDate && o.writtenDate !== '' && o.writtenDate !== '1901-01-01') {
      d = new Date(o.writtenDate);
    } else if (o.date && o.date !== '' && o.date !== '1901-01-01') {
      d = new Date(o.date);
    } else if (o.createdAt?.seconds) {
      d = new Date(o.createdAt.seconds * 1000);
    } else return false;
    return d >= start && d <= end;
  });

  // Only include reps that exist in our system (have a repId) or whose name doesn't start with SYN_
  // Build rep stats
  const repMap = {};
  filteredOrders.forEach(o => {
    const name = o.repName || 'Unknown';
    if (!repMap[name]) repMap[name] = { name, deals: 0, completed: 0, cancelled: 0, commission: 0, products: {} };
    repMap[name].deals++;
    if (o.status === 'Completed') repMap[name].completed++;
    if (o.status === 'Cancelled') repMap[name].cancelled++;
    const myComm = (o.commissions || []).find(c => c.repName === name);
    repMap[name].commission += myComm?.amount || o.totalCommission || 0;
    const prod = o.productName?.split(' ')[0] || 'Other';
    repMap[name].products[prod] = (repMap[name].products[prod] || 0) + 1;
  });

  const repNames = new Set(reps.map(r => r.name));
  const ranked = Object.values(repMap)
    .filter(r => {
      // Exclude generic SYN_ group names that aren't real reps
      if (r.name.startsWith('SYN_') && r.name.includes('Generic')) return false;
      if (r.name.startsWith('SYN_') && r.name.includes(',')) return false;
      // Only include if they exist in our reps list, or include all if no reps loaded yet
      return repNames.size === 0 || repNames.has(r.name);
    })
    .filter(r => r.deals > 0)
    .sort((a, b) => {
      if (sortBy === 'commission') return b.commission - a.commission;
      if (sortBy === 'completed') return b.completed - a.completed;
      return b.deals - a.deals;
    });

  const maxDeals = ranked[0]?.deals || 1;

  const periodLabel = {
    month: format(new Date(), 'MMMM yyyy'),
    lastmonth: format(subMonths(new Date(), 1), 'MMMM yyyy'),
    week: 'Last 7 days',
    alltime: 'All time',
  }[period];

  if (loading) return <div style={{ padding: 60, color: '#555' }}>Loading...</div>;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Stack ranking</h1>
          <p style={S.sub}>{ranked.length} reps · {periodLabel}</p>
        </div>
        <div style={S.controls}>
          <select style={S.select} value={period} onChange={e => setPeriod(e.target.value)}>
            <option value="month">This month</option>
            <option value="lastmonth">Last month</option>
            <option value="week">Last 7 days</option>
            <option value="alltime">All time</option>
          </select>
          <select style={S.select} value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="deals">Sort: deals written</option>
            <option value="completed">Sort: completed</option>
            <option value="commission">Sort: commission</option>
          </select>
        </div>
      </div>

      {/* Top 3 podium */}
      {ranked.length >= 3 && (
        <div style={S.podium}>
          {ranked.slice(0, 3).map((rep, i) => (
            <div key={rep.name} style={{ ...S.podiumCard, borderColor: i === 0 ? '#f5a623' : i === 1 ? '#888' : '#cd7f32' }}>
              <div style={S.podiumMedal}>{MEDALS[i]}</div>
              <div style={S.podiumName}>{rep.name}</div>
              <div style={{ ...S.podiumValue, color: i === 0 ? '#f5a623' : '#fff' }}>
                {sortBy === 'commission' ? '$' + Math.round(rep.commission).toLocaleString() : sortBy === 'completed' ? rep.completed : rep.deals}
              </div>
              <div style={S.podiumLabel}>{sortBy === 'commission' ? 'earned' : sortBy === 'completed' ? 'completed' : 'deals'}</div>
            </div>
          ))}
        </div>
      )}

      {/* Full ranking table */}
      <div style={S.tableCard}>
        <table style={S.table}>
          <thead>
            <tr>{['Rank', 'Rep', 'Written', 'Completed', 'Cancelled', 'Cancel %', 'Commission', 'Progress'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {ranked.map((rep, i) => {
              const cancelRate = rep.deals ? ((rep.cancelled / rep.deals) * 100).toFixed(0) : 0;
              const pct = Math.round((rep.deals / maxDeals) * 100);
              return (
                <tr key={rep.name}>
                  <td style={{ ...S.td, color: i < 3 ? '#f5a623' : '#555', fontWeight: 600, width: 50 }}>#{i + 1}</td>
                  <td style={S.td}>
                    <div style={S.repCell}>
                      <div style={S.avatar}>{rep.name[0]}</div>
                      {rep.name}
                    </div>
                  </td>
                  <td style={S.td}>{rep.deals}</td>
                  <td style={{ ...S.td, color: TEAL }}>{rep.completed}</td>
                  <td style={{ ...S.td, color: rep.cancelled > 0 ? '#ff6b6b' : '#555' }}>{rep.cancelled}</td>
                  <td style={{ ...S.td, color: Number(cancelRate) > 20 ? '#ff6b6b' : '#888' }}>{cancelRate}%</td>
                  <td style={{ ...S.td, color: '#f5a623' }}>${Math.round(rep.commission).toLocaleString()}</td>
                  <td style={{ ...S.td, width: 120 }}>
                    <div style={S.barWrap}>
                      <div style={{ ...S.barFill, width: `${pct}%` }} />
                    </div>
                  </td>
                </tr>
              );
            })}
            {ranked.length === 0 && <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: '#444', padding: 32 }}>No data for this period</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 12, color: '#555', margin: '4px 0 0' },
  controls: { display: 'flex', gap: 8 },
  select: { background: '#13131a', border: '1px solid #2a2a3a', borderRadius: 8, padding: '7px 12px', color: '#ccc', fontSize: 12, outline: 'none', cursor: 'pointer' },
  podium: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 },
  podiumCard: { background: '#13131a', border: '2px solid', borderRadius: 12, padding: '20px', textAlign: 'center' },
  podiumMedal: { fontSize: 28, marginBottom: 8 },
  podiumName: { fontSize: 14, fontWeight: 500, color: '#ccc', marginBottom: 6 },
  podiumValue: { fontSize: 28, fontWeight: 700, letterSpacing: '-0.5px' },
  podiumLabel: { fontSize: 11, color: '#555', marginTop: 2 },
  tableCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 10, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #1e1e2a' },
  td: { padding: '10px 12px', fontSize: 13, color: '#ccc', borderBottom: '1px solid #111' },
  repCell: { display: 'flex', alignItems: 'center', gap: 8 },
  avatar: { width: 28, height: 28, borderRadius: '50%', background: '#1e2a24', border: '1px solid #00d4a0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#00d4a0', flexShrink: 0 },
  barWrap: { background: '#1a1a24', borderRadius: 4, height: 6, overflow: 'hidden' },
  barFill: { background: TEAL, height: '100%', borderRadius: 4, transition: 'width .3s' },
};
